
import { useState, useEffect } from "react";
import { useUserPreferences } from "./useUserPreferences";

type ImportanceColor = {
  bg: string;
  text: string;
};

type ImportanceColorSettings = {
  [key: number]: ImportanceColor;
};

// Default colors as fallback
const defaultColors: ImportanceColorSettings = {
  1: { bg: "bg-slate-200", text: "text-slate-700" },
  2: { bg: "bg-blue-200", text: "text-blue-700" },
  3: { bg: "bg-green-200", text: "text-green-700" },
  4: { bg: "bg-amber-200", text: "text-amber-700" },
  5: { bg: "bg-red-200", text: "text-red-700" },
};

export function useImportanceColors() {
  const { preferences } = useUserPreferences();
  const [colorSettings, setColorSettings] = useState<ImportanceColorSettings>(defaultColors);

  useEffect(() => {
    if (preferences?.importanceColors) {
      try {
        const customColors = JSON.parse(preferences.importanceColors);
        setColorSettings(prev => ({
          ...prev,
          ...customColors
        }));
      } catch (error) {
        console.error("Failed to parse importance colors:", error);
      }
    } else {
      // If no custom colors are set, ensure we're using the defaults
      setColorSettings(defaultColors);
    }
  }, [preferences]);

  // This function is used in the Badge components and returns an object with style properties
  // instead of just the CSS classes
  const getImportanceColor = (importance: number): { className: string; style: React.CSSProperties } => {
    const colors = colorSettings[importance] || defaultColors[importance] || defaultColors[1];
    
    // Extract hex colors from the Tailwind classes if they're in the format bg-[#hex] or text-[#hex]
    const extractHexColor = (colorClass: string): string | null => {
      const hexMatch = colorClass.match(/\[(#[A-Fa-f0-9]{3,6})\]/);
      return hexMatch ? hexMatch[1] : null;
    };
    
    const bgHex = extractHexColor(colors.bg);
    const textHex = extractHexColor(colors.text);
    
    // Create style object for direct color application
    const style: React.CSSProperties = {};
    
    if (bgHex) {
      style.backgroundColor = bgHex;
    }
    
    if (textHex) {
      style.color = textHex;
    }
    
    // Return both the Tailwind classes (for non-custom colors) and the style object
    return {
      className: `${colors.bg} ${colors.text}`,
      style
    };
  };

  // Determine if the current color setting is a custom one or the default
  const isDefaultColor = (importance: number): boolean => {
    const currentColor = colorSettings[importance];
    const defaultColor = defaultColors[importance];
    
    if (!currentColor || !defaultColor) return false;
    return currentColor.bg === defaultColor.bg && currentColor.text === defaultColor.text;
  };

  return {
    colorSettings,
    getImportanceColor,
    defaultColors,
    isDefaultColor
  };
}
